import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileDigit,
  IndianRupee,
  MapPin,
  Package,
  Phone,
  Receipt,
  RotateCcw,
  User,
} from 'lucide-react';
import { getBaseUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface Product {
  id: string;
  name: string;
  quantity: number;
  unitCost: number;
  originalCost?: number;
  mrp?: number;
  discount: number;
  sgst: number;
  cgst: number;
  igst: number;
  cess?: number;
  taxableValue: number;
  totalAmount: number;
  serialNumber?: string;
  returnQty?: number;
  productCode?: number;
  barcode?: string;
  hsnCode?: string;
  /** 1 = IGST, 0 = SGST+CGST */
  isIGST?: number;
  sgstPer?: number;
  cgstPer?: number;
  igstPer?: number;
  cessPer?: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  companyName: string;
  createdOn: string;
  name: string;
  buyerName: string;
  customerCompany?: string;
  phone: string;
  email?: string;
  discount: number;
  amount: number;
  location: string;
  products: Product[];
}

interface RefundInvoiceDetailsPageProps {
  token: string;
}

const RefundInvoiceDetailsPage: React.FC<RefundInvoiceDetailsPageProps> = ({ token }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceFromState = location.state?.invoice as Invoice | undefined;

  const [invoice, setInvoice] = useState<Invoice | null>(invoiceFromState ?? null);
  const [products, setProducts] = useState<Product[]>(invoiceFromState?.products ?? []);
  const [loadingProducts, setLoadingProducts] = useState(!invoiceFromState?.products?.length);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  // Redirect if no invoice in state
  useEffect(() => {
    if (!invoiceFromState) {
      window.dispatchEvent(new CustomEvent('showRefunds', { detail: { refresh: true } }));
      navigate('/', { state: { showRefunds: true } });
      return;
    }
    setInvoice(invoiceFromState);
    if (invoiceFromState.products?.length) {
      setProducts(invoiceFromState.products);
      setLoadingProducts(false);
    }
  }, [invoiceFromState, navigate]);

  // Fetch line items when invoice is set and has no products
  useEffect(() => {
    if (!invoice?.id || products.length > 0) return;

    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/InvoiceLineItemsForInvoiceID?InvoiceID=${invoice.id}`;

    setLoadingProducts(true);
    fetch(url, {
      headers: { Authorization: authToken, Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((result) => {
        console.log('=== GET api/InvoiceLineItemsForInvoiceID ===');
        console.log('URL:', url);
        console.log('Response JSON:', JSON.stringify(result, null, 2));
        console.log('===========================================');

        if (result.status === 0 && Array.isArray(result.data)) {
          const mapped: Product[] = result.data.map((item: any) => {
            const isIGST = Number(item.isIGST ?? 0);
            const taxable = Number(item.Taxable || Number(item.UnitCost || 0) * Number(item.Qty || 0));
            const sgstAmt = Number(item.SGST || 0);
            const cgstAmt = Number(item.CGST || 0);
            const igstAmt = Number(item.IGST || 0);
            const cessAmt = Number(item.Cess || 0);

            // Use API percentage fields if present, otherwise derive from amounts
            let sgstPer: number;
            let cgstPer: number;
            let igstPer: number;
            let cessPer: number;

            // API returns sgstpercent, cgstpercent, igstpercent, cesspercent (InvoiceLineItemsForInvoiceID)
            const hasPercentFields = item.sgstpercent != null || item.cgstpercent != null || item.igstpercent != null || item.cesspercent != null;
            if (hasPercentFields) {
              sgstPer = Number(item.sgstpercent ?? item.SgstPer ?? item.sgstper ?? 0);
              cgstPer = Number(item.cgstpercent ?? item.CGstPer ?? item.cgstper ?? 0);
              igstPer = Number(item.igstpercent ?? item.IgstPer ?? item.igstper ?? 0);
              cessPer = Number(item.cesspercent ?? item.CessPer ?? item.cessper ?? item.Cess ?? 0);
            } else {
              // Derive percentages from amounts: percent = (amount / taxable) * 100
              if (isIGST === 1) {
                igstPer = taxable > 0 ? (igstAmt / taxable) * 100 : 0;
                sgstPer = 0;
                cgstPer = 0;
              } else {
                sgstPer = taxable > 0 ? (sgstAmt / taxable) * 100 : 0;
                cgstPer = taxable > 0 ? (cgstAmt / taxable) * 100 : 0;
                igstPer = 0;
              }
              cessPer = taxable > 0 ? (cessAmt / taxable) * 100 : Number(item.CessPer ?? item.cessper ?? 0);
            }

            return {
              id: String(item.Id || Math.random()),
              name: item['Product Name'] || item.ProductName || '-',
              quantity: Number(item.Qty || 0),
              unitCost: Number(item.UnitCost || 0),
              originalCost: Number(item.OriginalCost ?? item.originalCost ?? 0),
              mrp: Number(item.MRP ?? item.Mrp ?? 0),
              discount: Number(item.Discount || 0),
              sgst: sgstAmt,
              cgst: cgstAmt,
              igst: igstAmt,
              cess: cessAmt,
              taxableValue: taxable,
              totalAmount: Number(item.Total || 0),
              serialNumber: item.SerialNumber || undefined,
              returnQty: item.ReturnQty != null ? Number(item.ReturnQty) : undefined,
              productCode: item.ProductCode ? Number(item.ProductCode) : undefined,
              barcode: item.Barcode != null ? String(item.Barcode) : undefined,
              hsnCode: item.HSNCode != null ? String(item.HSNCode) : undefined,
              isIGST,
              sgstPer,
              cgstPer,
              igstPer,
              cessPer,
            };
          });
          setProducts(mapped);
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [invoice?.id, token, products.length]);

  const handleBackToRefunds = () => {
    window.dispatchEvent(new CustomEvent('showRefunds', { detail: { refresh: true } }));
    navigate('/', { state: { showRefunds: true, refresh: true } });
  };

  // Android / mobile web back: go back to Refunds screen (only on mobile - desktop unchanged)
  useBackNavigation({
    onBack: handleBackToRefunds,
    canGoBack: true,
    showExitConfirmation: false,
  });

  const toggleProduct = (productId: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const { toast } = useToast();

  const handleProcessReturn = () => {
    if (!invoice || selectedProductIds.size === 0) return;
    const selectedProductsList = products.filter((p) => selectedProductIds.has(p.id));

    // ReturnQty comes as negative from API (-1 = 1 returned). Normalize to positive.
    const returnQtyNorm = (p: Product) =>
      p.returnQty != null ? Math.abs(Number(p.returnQty)) : 0;

    // Filter out products that are already fully returned - don't add them to Return Processing
    const fullyReturnedProducts = selectedProductsList.filter(
      (p) => returnQtyNorm(p) >= p.quantity
    );
    const productsToProcess = selectedProductsList.filter(
      (p) => returnQtyNorm(p) < p.quantity
    );

    if (fullyReturnedProducts.length > 0) {
      const names = fullyReturnedProducts.map((p) => p.name).join(', ');
      toast({
        title: 'Already refunded',
        description: `These items are already fully returned and were excluded: ${names}`,
        variant: 'destructive',
      });
    }

    // Don't navigate if no products left to process
    if (productsToProcess.length === 0) {
      return;
    }

    navigate('/return-process', {
      state: {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        customerName: invoice.name,
        date: invoice.createdOn,
        location: invoice.location,
        products: productsToProcess,
        invoice, // pass full invoice so Back from return-process can return to this page
      },
    });
  };

  if (!invoice) {
    return null;
  }

  const selectedCount = selectedProductIds.size;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/80 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-slate-200/60 bg-white/70 backdrop-blur-xl px-4 py-4 md:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToRefunds}
              className="h-9 w-9 md:h-10 md:w-10 rounded-xl"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5 text-slate-600" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-800">Invoice Details</h1>
              <p className="text-sm text-slate-500 hidden md:block">View details and select items for return</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6 space-y-6">
        {/* Invoice summary card */}
        <Card className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md shadow-lg shadow-slate-200/40 overflow-hidden">
          <CardContent className="p-4 md:p-6">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Invoice summary
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <FileDigit className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Invoice No</p>
                  <p className="font-semibold text-slate-800">{invoice.invoiceNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-semibold text-slate-800">{invoice.createdOn}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Company</p>
                  <p className="font-semibold text-slate-800 whitespace-normal break-words">{invoice.companyName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500"> Name/Company</p>
                  <p className="font-semibold text-slate-800">{invoice.name}</p>
                  {invoice.name && (
                    <p className="text-sm text-slate-600">{invoice.customerCompany ?? ''}</p> 
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="font-semibold text-slate-800">{invoice.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="font-semibold text-slate-800">{invoice.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Total amount</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-semibold bg-slate-100 text-slate-800 border border-slate-200/60">
                  <IndianRupee className="h-3.5 w-3.5" />
                  {invoice.amount.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product Details */}
        <Card className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md shadow-lg shadow-slate-200/40 overflow-hidden">
          <CardContent className="p-4 md:p-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-200/60 flex items-center justify-center">
                <Package className="h-4 w-4 text-slate-600" />
              </div>
              Product Details
              {loadingProducts && (
                <span className="ml-2 text-xs text-slate-500 animate-pulse">Loading...</span>
              )}
            </h2>

            {loadingProducts ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-slate-600 animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <p className="text-slate-500 text-sm py-6">No line items found for this invoice.</p>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-3">Select products to include in the return.</p>
                {/* Desktop table */}
                <div className="hidden md:block rounded-xl border border-slate-200/60 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100/80 border-b border-slate-200/60">
                      <tr>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-600 w-10">Select</th>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-600">Product</th>
                        <th className="px-3 py-2.5 text-center font-medium text-slate-600">Qty</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-600">Unit Cost</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-600">Discount</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-600">Total</th>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-600">Serial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => {
                        const isSelected = selectedProductIds.has(product.id);
                        const returnQtyNorm = product.returnQty != null ? Math.abs(Number(product.returnQty)) : 0;
                        const isFullyReturned = returnQtyNorm >= product.quantity;
                        return (
                          <tr
                            key={product.id}
                            onClick={() => !isFullyReturned && toggleProduct(product.id)}
                            className={cn(
                              'border-b border-slate-100 last:border-b-0 transition-colors',
                              isFullyReturned ? 'bg-rose-50/60 cursor-not-allowed' : 'cursor-pointer',
                              !isFullyReturned && (isSelected ? 'bg-blue-50/80' : 'hover:bg-slate-50/80')
                            )}
                          >
                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => !isFullyReturned && toggleProduct(product.id)}
                                disabled={isFullyReturned}
                              />
                            </td>
                            <td className="px-3 py-2.5 font-medium text-slate-800">
                              <div className="flex flex-col gap-0.5">
                                <span>{product.name}</span>
                                {isFullyReturned && (
                                  <span className="text-xs font-medium text-rose-600">Product already returned</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-center text-slate-600">{product.quantity}</td>
                            <td className="px-3 py-2.5 text-right">₹{product.unitCost.toFixed(2)}</td>
                            <td className="px-3 py-2.5 text-right">₹{product.discount.toFixed(2)}</td>
                            <td className="px-3 py-2.5 text-right font-semibold text-slate-800">₹{product.totalAmount.toFixed(2)}</td>
                            <td className="px-3 py-2.5 text-xs text-slate-500">{product.serialNumber || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {products.map((product) => {
                    const isSelected = selectedProductIds.has(product.id);
                    const returnQtyNorm = product.returnQty != null ? Math.abs(Number(product.returnQty)) : 0;
                    const isFullyReturned = returnQtyNorm >= product.quantity;
                    return (
                      <div
                        key={product.id}
                        onClick={() => !isFullyReturned && toggleProduct(product.id)}
                        className={cn(
                          'rounded-xl border p-3 transition-all',
                          isFullyReturned ? 'bg-rose-50/60 border-rose-200/60 cursor-not-allowed' : 'cursor-pointer',
                          !isFullyReturned && (isSelected ? 'bg-blue-50/80 border-blue-300/60' : 'bg-white/80 border-slate-200/60')
                        )}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-slate-800">{product.name}</div>
                            {isFullyReturned && (
                              <span className="text-xs font-medium text-rose-600">Product already returned</span>
                            )}
                          </div>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => !isFullyReturned && toggleProduct(product.id)}
                            disabled={isFullyReturned}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div><span className="text-slate-500">Qty</span> {product.quantity}</div>
                          <div><span className="text-slate-500">Unit Cost</span> ₹{(product.mrp ?? product.unitCost).toFixed(2)}</div>
                          <div><span className="text-slate-500">Total</span> <span className="font-semibold text-slate-800">₹{product.totalAmount.toFixed(2)}</span></div>
                          {product.serialNumber && (
                            <div className="col-span-2 text-slate-500 truncate">Serial: {product.serialNumber}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-200/60 bg-white/70 backdrop-blur-xl px-4 py-3 md:px-6 md:py-4 shadow-[0_-4px_24px_-4px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <p className="text-sm text-slate-500">
            {selectedCount > 0
              ? `${selectedCount} ${selectedCount === 1 ? 'product' : 'products'} selected for return`
              : 'Select at least one product to process return'}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleBackToRefunds} className="rounded-xl">
              Back to list
            </Button>
            <Button
              onClick={handleProcessReturn}
              disabled={selectedCount === 0}
              className="min-w-[140px] rounded-xl bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white shadow-lg shadow-slate-800/25 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Process Return
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundInvoiceDetailsPage;
